import { AnimationFacet, AnimationStateMessage, ClientReloadSessionMessage, ControlAnimationMessage, DeleteModelMessage, MulticastDelegate1, RequestAnimationStateMessage } from "flavr-common";
import { AnimationGroup } from "./AnimationGroup";
import { modelPool } from "@modules/model/ModelPool";
import { busService } from "@modules/bus";
class AnimationRegistry {
    constructor() {
        this.controls = new Map();
        this.subscriptions = new Map();
        this.onChanged = new MulticastDelegate1();
        this.connection = busService.connection("AnimationRegistry");
        this.states = new Map();
        this.pendingPauses = new Map();
        this.connection.subscribeByType(AnimationStateMessage, (message) => {
            this.receiveState(message);
        });
        this.connection.subscribeByType(DeleteModelMessage, (message) => {
            this.states.delete(message.uid);
            this.pendingPauses.delete(message.uid);
        });
        this.connection.subscribeByType(ClientReloadSessionMessage, () => {
            this.states.clear();
            this.pendingPauses.clear();
        });
    }
    command(uid, command, clip) {
        const state = this.states.get(uid);
        const controls = this.controls.get(uid);
        if (command === "pause" && this.pendingPauses.has(uid))
            return;
        if (command === "pause" && (state === null || state === void 0 ? void 0 : state.playing) && controls && controls.selectedAnimationClip !== null) {
            const position = controls.animationTime;
            const paused = Object.assign(Object.assign({}, state), { position, playing: false });
            this.pendingPauses.set(uid, paused);
            controls.applyAnimationState(paused);
            this.onChanged.invoke(uid);
            this.connection.ask(new ControlAnimationMessage(uid, command, clip, { playbackId: state.playbackId, position }), (messages) => {
                var _a;
                const answers = messages.filter(AnimationStateMessage.is);
                if (answers.length > 0) {
                    for (const answer of answers)
                        this.receiveState(answer);
                    return;
                }
                console.error("[animation] Pause failed", messages);
                if (this.pendingPauses.get(uid) !== paused)
                    return;
                this.pendingPauses.delete(uid);
                const current = this.states.get(uid);
                if (current)
                    (_a = this.controls.get(uid)) === null || _a === void 0 ? void 0 : _a.applyAnimationState(current);
                this.onChanged.invoke(uid);
            });
            return;
        }
        this.connection.send(new ControlAnimationMessage(uid, command, clip));
    }
    receiveState(message) {
        var _a;
        const previous = this.states.get(message.uid);
        if (previous && previous.revision > message.state.revision)
            return;
        const pending = this.pendingPauses.get(message.uid);
        if (pending && pending.playbackId === message.state.playbackId &&
            message.state.revision <= pending.revision && message.state.playing)
            return;
        this.pendingPauses.delete(message.uid);
        this.states.set(message.uid, message.state);
        (_a = this.controls.get(message.uid)) === null || _a === void 0 ? void 0 : _a.applyAnimationState(message.state);
        this.onChanged.invoke(message.uid);
    }
    selectClip(uid, index, saveDefault = false, settingsUID = uid) {
        var _a;
        const controls = this.controls.get(uid);
        if (!controls)
            throw new Error(`[animation] Model ${uid} has no animation controls`);
        const info = index === null ? undefined : controls.animationClipInfo.find((clip) => clip.key === controls.animationClipIds[index]);
        if (index !== null && !info)
            throw new Error(`[animation] Invalid clip index ${index}`);
        if (saveDefault) {
            const settings = this.getSettings(settingsUID);
            this.setSettings(settingsUID, new AnimationFacet(settings.loop, settings.playOnStart, info === null || info === void 0 ? void 0 : info.name, (_a = info === null || info === void 0 ? void 0 : info.occurrence) !== null && _a !== void 0 ? _a : 0, index !== null));
        }
        this.command(uid, "select", info ? { name: info.name, occurrence: info.occurrence } : null);
    }
    requestState(uid, controls) {
        var _a;
        const state = (_a = this.pendingPauses.get(uid)) !== null && _a !== void 0 ? _a : this.states.get(uid);
        if (state)
            controls.applyAnimationState(state);
        this.connection.send(new RequestAnimationStateMessage(uid, [...controls.animationClipInfo]));
    }
    get(uid) {
        return this.controls.get(uid);
    }
    getFacetTargets(uid) {
        const model = modelPool.getModel(uid);
        if (!model)
            return [];
        const controls = this.controls.get(uid);
        if (controls && controls.animationClips.length > 0) {
            return [{ uid, name: model.getName(), controls }];
        }
        const targets = [];
        for (const child of model.getChildren()) {
            if (child.getFacet(AnimationFacet))
                continue;
            const childControls = this.controls.get(child.getUID());
            if (childControls && childControls.animationClips.length > 0) {
                targets.push({ uid: child.getUID(), name: child.getName(), controls: childControls });
            }
        }
        return targets;
    }
    register(uid, controls) {
        this.controls.set(uid, controls);
        this.attachSettings(uid, controls);
        this.requestState(uid, controls);
        this.onChanged.invoke(uid);
    }
    registerGroupMember(uid, member) {
        const existing = this.controls.get(uid);
        if (existing && !(existing instanceof AnimationGroup)) {
            throw new Error(`[mesh] Model ${uid} already has independent animation controls`);
        }
        const group = existing !== null && existing !== void 0 ? existing : new AnimationGroup();
        this.controls.set(uid, group);
        group.add(member);
        if (!existing)
            this.attachSettings(uid, group);
        this.requestState(uid, group);
        this.onChanged.invoke(uid);
    }
    unregister(uid, controls) {
        var _a;
        const existing = this.controls.get(uid);
        if (existing instanceof AnimationGroup) {
            existing.remove(controls);
            if (existing.size === 0)
                this.controls.delete(uid);
        }
        else {
            if (existing !== controls)
                return;
            this.controls.delete(uid);
        }
        if (!this.controls.has(uid)) {
            for (const unsubscribe of (_a = this.subscriptions.get(uid)) !== null && _a !== void 0 ? _a : [])
                unsubscribe();
            this.subscriptions.delete(uid);
        }
        this.onChanged.invoke(uid);
    }
    getSettings(uid) {
        var _a, _b;
        return (_b = (_a = modelPool.getModel(uid)) === null || _a === void 0 ? void 0 : _a.getFacet(AnimationFacet)) !== null && _b !== void 0 ? _b : new AnimationFacet();
    }
    setSettings(uid, settings) {
        const model = modelPool.getModel(uid);
        if (!model)
            throw new Error(`[animation] Model ${uid} no longer exists`);
        model.setFacet(settings);
    }
    attachSettings(uid, controls) {
        var _a;
        for (const unsubscribe of (_a = this.subscriptions.get(uid)) !== null && _a !== void 0 ? _a : [])
            unsubscribe();
        const model = modelPool.getModel(uid);
        if (!model)
            throw new Error(`[animation] Model ${uid} is missing while registering playback`);
        const settings = this.getSettings(uid);
        controls.setAnimationLoop(settings.loop);
        // Wait for the server's shared start time instead of starting separately on every client.
        controls.pauseAnimation();
        this.subscriptions.set(uid, [
            model.setOnFacetAddedHandler((changedModel, facet) => {
                if (changedModel.getUID() !== uid || !AnimationFacet.is(facet))
                    return;
                controls.setAnimationLoop(facet.loop);
                this.onChanged.invoke(uid);
            }),
            model.setOnFacetRemovedHandler((changedModel, facet) => {
                if (changedModel.getUID() !== uid || !AnimationFacet.is(facet))
                    return;
                controls.setAnimationLoop(true);
                this.onChanged.invoke(uid);
            })
        ]);
    }
}
export const animationRegistry = new AnimationRegistry();
